import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { convitesService } from "@/core/services/convitesService";
import { useAuth } from "@/core/hooks/useAuth";

const conviteKey = (pessoaId: string) => ["meus-convites", pessoaId];

/**
 * Convites de ministério pendentes da pessoa logada (DA-003). Alimenta o
 * card do Início e a bolinha/feed do sino de notificações.
 */
export function useMeusConvites() {
  const { pessoa } = useAuth();
  return useQuery({
    queryKey: conviteKey(pessoa?.id ?? ""),
    queryFn: () => convitesService.meusConvites(pessoa!.id),
    enabled: !!pessoa,
    refetchInterval: 60_000,
  });
}

/**
 * Aceitar/recusar convite. Além das queries, recarrega os portões de acesso
 * do AuthContext: entrar num ministério pode mudar módulos visíveis e até a
 * gestão de módulo (se a função convidada tiver pode_gerenciar_modulo) —
 * sem isso, a pessoa só veria o efeito no próximo login.
 */
export function useResponderConvite() {
  const queryClient = useQueryClient();
  const { pessoa, recarregarAcessos } = useAuth();
  return useMutation({
    mutationFn: ({ vinculoId, aceitar }: { vinculoId: string; aceitar: boolean }) =>
      aceitar ? convitesService.aceitar(vinculoId) : convitesService.recusar(vinculoId),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: conviteKey(pessoa?.id ?? "") });
      queryClient.invalidateQueries({ queryKey: ["ministerios"] });
      // Aceitar pode tornar a pessoa líder ou Gestor de elo: os cartões do Início e as lições da
      // Ajuda dependem disso e precisam refletir já, sem esperar o próximo login.
      queryClient.invalidateQueries({ queryKey: ["ministerios-que-lidero"] });
      queryClient.invalidateQueries({ queryKey: ["ministerios-que-gerencio"] });
      queryClient.invalidateQueries({ queryKey: ["ajuda-elos-gestao"] });
      await recarregarAcessos();
    },
  });
}

/**
 * Candidatas a convite/cadastro num ministério (admin ou líder — RPC 0024).
 * `funcaoId` refaz a busca quando a função escolhida no formulário muda —
 * permite que alguém já integrante apareça de novo ao oferecer uma função
 * diferente da que ele já tem (uma pessoa pode ter mais de uma função).
 */
export function usePessoasParaConvite(
  ministerioId: string | undefined,
  habilitado: boolean,
  funcaoId?: string,
) {
  return useQuery({
    queryKey: ["pessoas-para-convite", ministerioId ?? "", funcaoId ?? ""],
    queryFn: () => convitesService.listarPessoasParaConvite(ministerioId as string, funcaoId),
    enabled: !!ministerioId && habilitado,
  });
}
